// This provider is made in-house instead of using the featurehub-react-sdk becasuse the
// latter depends on react 18.3.0 and upgrading would be a huge chore
import { EdgeFeatureHubConfig, ClientContext } from "featurehub-javascript-client-sdk";
import React, { createContext, useContext, useEffect, useState } from "react";

import { useSelector } from "@/store";

// Define the type for your context state
interface FeatureHubContextType {
    context: ClientContext | null;
}

// Create the context with a default value
const FeatureHubContext = createContext<FeatureHubContextType | undefined>(undefined);

interface FeatureHubProviderProps {
    edgeUrl: string;
    apiKey: string;
    children: React.ReactNode;
}

// FeatureHub Provider component
export const FeatureHubProvider: React.FC<FeatureHubProviderProps> = ({ edgeUrl, apiKey, children }) => {
    const { username, lvtu, lvtc } = useSelector((state) => state.user);
    const [context, setContext] = useState<FeatureHubContextType>({ context: null });

    useEffect(() => {
        if (username) {
            // Initialize the FeatureHub configuration
            const config = new EdgeFeatureHubConfig(edgeUrl, apiKey);

            // TODO: JET-60999 enable SSE streaming once featurehub is public
            // for now, we are proxying through lvt-ui and polling
            // config.streaming();

            const ctx = config.newContext();

            ctx.userKey(username.toLowerCase());
            ctx.attributeValue("clientId", lvtc ?? "unknown");
            ctx.attributeValue("userId", lvtu ?? "unknown");
            ctx.build();

            setContext({ context: ctx });

            function newStateListener() {
                setContext({ context: ctx });
            }

            // context should react to updates by resetting the state
            config.repository().addPostLoadNewFeatureStateAvailableListener(newStateListener);

            return () => {
                ctx.clear();
                config.repository().removePostLoadNewFeatureStateAvailableListener(newStateListener);
            };
        }
    }, [edgeUrl, apiKey, username, lvtu, lvtc]);

    return <FeatureHubContext.Provider value={context}>{children}</FeatureHubContext.Provider>;
};

// Custom hook to use the FeatureHub context
export const useFeatureHub = (): FeatureHubContextType => {
    const context = useContext(FeatureHubContext);

    if (context === undefined) {
        throw new Error("useFeatureHub must be used within a FeatureHubProvider");
    }

    return context;
};

export const Flags = {
    alerts: "next_alerts",
    alertsAllowlist: "next_alerts_allowlist",
    forensic_search: "next_forensic_search",
    guard_gate_all_functionality: "guard_gate_all_functionality",
    reports: "next_reports",
    video_library: "next_video_library",
};
